import React, { useEffect, useRef } from 'react';

const CookwarePage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const productCategories = [
    {
      title: "Traditional Cookware",
      items: ["Kadhai", "Handis", "Biryani Handis", "Topes"],
      icon: "fas fa-fire",
      description: "Authentic traditional cooking vessels for cultural cuisine"
    },
    {
      title: "Modern Kitchen",
      items: ["Saucepan", "Frypan", "Pressure Cookers", "Non-stick (Coming Soon)"],
      icon: "fas fa-kitchen-set",
      description: "Contemporary cookware for modern cooking needs"
    },
    {
      title: "Utility Items",
      items: ["Boya/Stainer", "Buckets", "Idli Pot", "Storage Containers"],
      icon: "fas fa-utensils",
      description: "Essential kitchen utilities and accessories"
    },
    {
      title: "Cast Products",
      items: ["Handles", "Cast Karahi", "Tawa", "Custom Castings"],
      icon: "fas fa-hammer",
      description: "Specialized cast aluminium products and components"
    }
  ];

  const features = [
    {
      title: "Induction Compatible",
      description: "Available with induction bottom for modern cooking requirements",
      icon: "fas fa-bolt"
    },
    {
      title: "Food Grade Aluminium",
      description: "Safe, non-toxic materials suitable for food contact",
      icon: "fas fa-leaf"
    },
    {
      title: "Durable Construction",
      description: "Heavy-duty construction for long-lasting performance",
      icon: "fas fa-shield-alt"
    },
    {
      title: "Heat Distribution",
      description: "Excellent heat conduction for even cooking results",
      icon: "fas fa-fire"
    }
  ];

  const upcomingProducts = [
    {
      title: "Non-stick Cookware",
      description: "Advanced non-stick coating technology",
      icon: "fas fa-magic",
      status: "Coming Soon"
    },
    {
      title: "Hard-anodized Products",
      description: "Superior durability and scratch resistance",
      icon: "fas fa-gem",
      status: "In Development"
    },
    {
      title: "Pressure Cookers",
      description: "Modern pressure cooking solutions",
      icon: "fas fa-tachometer-alt",
      status: "Coming Soon"
    }
  ];

  return (
    <div className="cookware-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .cookware-page {
          font-family: 'Inter', 'Poppins', sans-serif;
          line-height: 1.6;
          color: #1E2329;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Product Showcase */
        .product-showcase {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .showcase-image-container {
          position: relative;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
          margin-bottom: 3rem;
          background: white;
        }

        .showcase-image-container img {
          width: 100%;
          height: 500px;
          object-fit: cover;
          transition: all 0.3s ease;
        }

        .showcase-image-container:hover img {
          transform: scale(1.05);
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          color: white;
          padding: 2rem;
          transform: translateY(100%);
          transition: transform 0.4s ease;
        }

        .showcase-image-container:hover .image-overlay {
          transform: translateY(0);
        }

        .image-overlay h3 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .image-overlay p {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .showcase-image-placeholder {
          width: 100%;
          height: 500px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px dashed #7B3FE4;
        }

        .image-placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .image-placeholder-content i {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          display: block;
        }

        .image-placeholder-text {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .image-placeholder-desc {
          font-size: 1rem;
          opacity: 0.8;
          max-width: 400px;
        }

        /* Product Gallery Section */
        .gallery-section {
          background: white;
          padding: 100px 0;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
        }

        .gallery-item {
          background: white;
          border-radius: 20px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
        }

        .gallery-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
        }

        .gallery-item:hover {
          transform: translateY(-10px);
          border-color: rgba(123, 63, 228, 0.3);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .gallery-image-placeholder {
          width: 100%;
          height: 280px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 2px dashed #7B3FE4;
          transition: all 0.3s ease;
        }

        .gallery-item:hover .gallery-image-placeholder {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
        }

        .gallery-image-placeholder .image-placeholder-content i {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: block;
        }

        .gallery-image-placeholder .image-placeholder-text {
          font-size: 1rem;
          font-weight: 500;
        }

        .gallery-caption {
          padding: 2rem;
          text-align: center;
        }

        .gallery-caption h4 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 0.5rem;
        }

        .gallery-caption p {
          color: #4A5260;
          font-size: 1rem;
        }

        /* Product Categories Section */
        .categories-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2.5rem;
        }

        .category-card {
          background: white;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
          overflow: hidden;
        }

        .category-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
        }

        .category-card:hover {
          transform: translateY(-10px);
          border-color: rgba(123, 63, 228, 0.3);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .category-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .category-card:hover .category-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .category-icon i {
          font-size: 2rem;
          color: white;
        }

        .category-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .category-description {
          color: #4A5260;
          margin-bottom: 1.5rem;
          line-height: 1.7;
        }

        .category-items {
          list-style: none;
          text-align: left;
        }

        .category-items li {
          padding: 0.5rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .category-items li::before {
          content: '▶';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-size: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
        }

        /* Features Section */
        .features-section {
          padding: 100px 0;
          background: white;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2.5rem;
        }

        .feature-card {
          background: #F7FAFC;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
        }

        .feature-card:hover {
          border-color: #7B3FE4;
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(123, 63, 228, 0.15);
        }

        .feature-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #9A60FF, #7B3FE4);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }

        .feature-icon i {
          font-size: 2rem;
          color: white;
        }

        .feature-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .feature-card p {
          color: #4A5260;
          line-height: 1.7;
        }

        /* Upcoming Products Section */
        .upcoming-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .upcoming-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
        }

        .upcoming-card {
          background: white;
          padding: 3rem 2.5rem;
          border-radius: 20px;
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
          overflow: hidden;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
        }

        .upcoming-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #9A60FF, #14B87A);
        }

        .upcoming-card:hover {
          border-color: #9A60FF;
          transform: translateY(-8px);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
        }

        .upcoming-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #9A60FF, #14B87A);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .upcoming-card:hover .upcoming-icon {
          transform: scale(1.1);
        }

        .upcoming-icon i {
          font-size: 2rem;
          color: white;
        }

        .upcoming-card h3 {
          font-size: 1.4rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .upcoming-card p {
          color: #4A5260;
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .status-badge {
          display: inline-block;
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          color: white;
          border-radius: 25px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1E2329 0%, #4A5260 100%);
          text-align: center;
          color: white;
        }

        .cta-content h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }

        .cta-content p {
          font-size: 1.3rem;
          margin-bottom: 2.5rem;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-block;
          padding: 18px 40px;
          background: white;
          color: #7B3FE4;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.4s ease;
          cursor: pointer;
          border: none;
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.2);
        }

        .cta-button:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 15px 40px rgba(255, 255, 255, 0.3);
        }

        .cta-button-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .cta-button-secondary:hover {
          background: white;
          color: #7B3FE4;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .gallery-grid {
            grid-template-columns: 1fr;
          }

          .showcase-image-placeholder {
            height: 300px;
          }

          .image-placeholder-content i {
            font-size: 3rem;
          }

          .image-placeholder-text {
            font-size: 1.1rem;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section - Only background drone image */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-kitchen-set"></i>
          </div>
          <h1 className="animate-fade-in">Aluminium Cookware & Utensils</h1>
          <p className="hero-subtitle animate-fade-in">
            Comprehensive range of traditional and modern aluminium cookware manufactured with precision and quality
          </p>
        </div>
      </section>


      {/* Product Gallery Section - Image placeholders */}
      <section className="gallery-section">
        <div className="container">
          <div className="section-header">
            <h2>Product Gallery</h2>
            <p>Comprehensive collection of aluminium cookware and kitchen utensils</p>
          </div>
          <div className="gallery-grid">
            <div className="gallery-item animate-on-scroll">
              <img
                src="/images/Cookware/kadhai.jpg"
                alt="Traditional Cookware"
                loading="lazy"
                style={{width: '100%', height: '300px', objectFit: 'contain', borderRadius: '15px', backgroundColor: '#f8f9fa', padding: '10px'}}
              />
              <div className="gallery-caption">
                <h4>Kadhai & Handis Collection</h4>
                <p>Traditional cooking vessels for authentic cuisine</p>
              </div>
            </div>
            <div className="gallery-item animate-on-scroll">
              <img
                src="/images/Cookware/sausepan.jpg"
                alt="Modern Kitchen Items"
                loading="lazy"
                style={{width: '100%', height: '300px', objectFit: 'cover', borderRadius: '15px'}}
              />
              <div className="gallery-caption">
                <h4>Saucepan & Frypan</h4>
                <p>Contemporary cooking solutions</p>
              </div>
            </div>
            <div className="gallery-item animate-on-scroll">
              <img
                src="/images/Cookware/aluminium-bucket.jpg"
                alt="Utility Items"
                loading="lazy"
                style={{width: '100%', height: '300px', objectFit: 'cover', borderRadius: '15px'}}
              />
              <div className="gallery-caption">
                <h4>Boya, Buckets & Strainers</h4>
                <p>Essential kitchen utilities</p>
              </div>
            </div>
            <div className="gallery-item animate-on-scroll">
              <img
                src="/images/Cookware/Induction-bottom-for-Aluminum-cookware-2.jpg"
                alt="Induction Compatible"
                loading="lazy"
                style={{width: '100%', height: '300px', objectFit: 'cover', borderRadius: '15px'}}
              />
              <div className="gallery-caption">
                <h4>Induction Bottom Cookware</h4>
                <p>Modern induction-ready products</p>
              </div>
            </div>
            <div className="gallery-item animate-on-scroll">
              <img
                src="/images/Cookware/aluminium-casting-kadhai-handle-1000x1000.webp"
                alt="Cast Products"
                loading="lazy"
                style={{width: '100%', height: '300px', objectFit: 'contain', borderRadius: '15px', backgroundColor: '#f8f9fa', padding: '10px'}}
              />
              <div className="gallery-caption">
                <h4>Cast Handles & Components</h4>
                <p>Specialized cast aluminum products</p>
              </div>
            </div>
            <div className="gallery-item animate-on-scroll">
              <img
                src="/images/Cookware/cooker.jpg"
                alt="Pressure Cookers"
                loading="lazy"
                style={{width: '100%', height: '300px', objectFit: 'cover', borderRadius: '15px'}}
              />
              <div className="gallery-caption">
                <h4>Pressure Cooking Solutions</h4>
                <p>Coming soon - Modern pressure cookers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2>Product Categories</h2>
            <p>Comprehensive range of cookware and utensils for every kitchen need</p>
          </div>
          
          <div className="categories-grid">
            {productCategories.map((category, index) => (
              <div key={index} className="category-card animate-on-scroll">
                <div className="category-icon">
                  <i className={category.icon}></i>
                </div>
                <h3>{category.title}</h3>
                <p className="category-description">{category.description}</p>
                <ul className="category-items">
                  {category.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Key Features</h2>
            <p>Superior quality features that make our cookware stand out</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card animate-on-scroll">
                <div className="feature-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Products Section */}
      <section className="upcoming-section">
        <div className="container">
          <div className="section-header">
            <h2>Upcoming Innovations</h2>
            <p>Exciting new products in development to enhance your cooking experience</p>
          </div>
          
          <div className="upcoming-grid">
            {upcomingProducts.map((product, index) => (
              <div key={index} className="upcoming-card animate-on-scroll">
                <div className="upcoming-icon">
                  <i className={product.icon}></i>
                </div>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                <span className="status-badge">{product.status}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Discover Quality Cookware</h2>
            <p>
              Explore our comprehensive range of aluminium cookware and utensils. Contact us for bulk orders, custom requirements, and technical consultation.
            </p>
            <div className="cta-buttons">
              <button className="cta-button" onClick={() => window.location.href = '/contact/enquiry-form'}>
                Request Catalog
              </button>
              <button className="cta-button cta-button-secondary" onClick={() => window.location.href = '/businesses'}>
                View All Products
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>          
  );
};

export default CookwarePage;